import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { format, formatDistanceToNow } from 'date-fns';
import { useDocumentCharts } from '../../composables/useDocumentCharts.js';

export default () => {
  const store = useStore();
  const { t } = useI18n();
  const settings = computed(() => store.state.settings);

  const teamsObj = computed(() => store.getters.getTeamsObj);
  const usersObj = computed(() => store.getters.getUsersObj);

  const documents = ref([]);
  const currentFolderId = ref("");
  const rootFolderId = ref("");
  const loading = ref(true);
  const search = ref('');
  const searchLatest = ref('');
  const activeTab = ref('all');

  // The three charts' aggregations and the team → user drill state. The charts
  // themselves are @rdrive/plugin-chart-bar-line bases now and plot `[{ label, value }]`; every
  // string they render arrives translated from the template above.
  const {
    fileTypeMetric,
    fileTypeRows,
    uploadsMetric,
    uploadsRows,
    teamUserLevel,
    teamUserRows,
    teamUserColorIndex,
    selectTeamUser,
    formatSize,
    formatFileSize,
  } = useDocumentCharts({ documents, teamsObj, usersObj });

  // The three bare charts' PNG export: each card's title row holds a download button that calls
  // its chart's `downloadPng()` through these STRING template refs (`ref="…"` in component.vue —
  // they only bind because they are returned under these exact names). The team/user chart is a
  // skeleton while loading, so its ref is null then (the template calls it with `?.`).
  const teamUserChart = ref(null);
  const fileTypeChart = ref(null);
  const uploadsChart = ref(null);

  const allDocumentsHeaders = computed(() => [
        { title: t('name'), key: 'displayName' },
        { title: t('type'), key: 'fileType' },
        { title: t('size'), key: 'fileSize' },
        { title: t('date'), key: 'createdDate' },
        { title: t('folder'), key: 'parentFolderId' },
        { title: t('uploadedBy'), key: 'teamId' },
        { title: '', key: 'userId' }
      ]);

      const headers = computed(() => [
        { title: t('name'), key: 'displayName' },
        { title: t('type'), key: 'fileType' },
        { title: t('size'), key: 'fileSize' },
        { title: t('uploadedDate'), key: 'createdDate' },
        { title: t('uploadedBy'), key: 'teamId' },
        { title: '', key: 'userId' }
      ]);

  const getData = async () => {
    try {
      loading.value = true;
      documents.value = await store.state.apiClient.get('/api/' + store.state.settings.projectCode + '/documents');
      // parentFolderId can come back as "", null or missing for top-level entries
      const rootFolder = documents.value.find(doc => doc.isFolder && !doc.parentFolderId);
      if (rootFolder) {
        rootFolderId.value = rootFolder.id;
        currentFolderId.value = rootFolder.id;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      loading.value = false;
    }
  };

  const filteredLatestDocuments = computed(() => {
    let docs = documents.value.filter(doc => !doc.isFolder).sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
  
    if (searchLatest.value) {
      const searchLower = searchLatest.value.toLowerCase();
      docs = docs.filter(item => {
        return Object.values(item).some(value => 
          String(value).toLowerCase().includes(searchLower)
        );
      });
    }
  
    return docs;
  });

  const filteredDocuments = computed(() => {
    if (!search.value) return currentFolderContent.value;

    const searchLower = search.value.toLowerCase();
    return documents.value.filter(item => {
      return Object.values(item).some(value => 
        String(value).toLowerCase().includes(searchLower)
      );
    });
  });

  const recentDocumentsCount = computed(() => {
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return documents.value.filter(doc => !doc.isFolder && new Date(doc.createdDate) > twentyFourHoursAgo).length;
  });

  watch(
    () => [
      settings.value.scopedToken, 
      settings.value.projectCode
    ],
    ([newScopedToken, newProjectCode]) => {
      if (newScopedToken && newProjectCode) {
        getData();
      }
    }
  );

  const totalDocuments = computed(() => documents.value.filter(doc => !doc.isFolder).length);

  const totalStorage = computed(() => {
    return documents.value.reduce((total, doc) => total + doc.fileSize, 0);
  });

  const formatStorage = (bytes) => {
    if (bytes === undefined) {
     return 'N/A';
    } else if (bytes === 0) {
      return '-';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(2)} ${units[unitIndex]}`;
  };

  const currentFolderContent = computed(() => {
    if (search.value) return filteredDocuments.value;
  
    if (currentFolderId.value === "") {
      // No root folder record found: show all top-level entries, folders included,
      // so directories are still drillable
      return documents.value.filter(doc => !doc.parentFolderId);
    }
    return documents.value.filter(doc => doc.parentFolderId === currentFolderId.value);
  });

  const getParentFolderName = (parentFolderId) => {
    if (!parentFolderId) return t("Root");
    const parentFolder = documents.value.find(doc => doc.id === parentFolderId);
    return parentFolder ? parentFolder.displayName : "Unknown Folder";
  };

  const handleItemClick = (item) => {
    if (item.isFolder) {
      // An active search overrides the folder view, so drilling in must clear it
      // or the click has no visible effect
      search.value = '';
      currentFolderId.value = item.id;
    } else if (item.fileDownloadLink) {
      downloadFile(item);
    }
  };

  const downloadFile = (item) => {
    if (item.fileDownloadLink) {
      openUrl(item.fileDownloadLink);
    } else {
      console.warn('No download link available for this file.');
    }
  };

  const goToPath = (item) => {
    currentFolderId.value = item.id;
  };

  const breadcrumbs = computed(() => {
    const breadcrumbItems = [];
    let currentFolder = documents.value.find(doc => doc.id === currentFolderId.value);

    while (currentFolder && currentFolder.parentFolderId) {
      breadcrumbItems.unshift({
        title: currentFolder.displayName,
        disabled: false,
        href: 'javascript:void(0)',
        id: currentFolder.id
      });
      currentFolder = documents.value.find(doc => doc.id === currentFolder.parentFolderId);
    }

    breadcrumbItems.unshift({
      title: t('root'),
      disabled: currentFolderId.value === "",
      href: 'javascript:void(0)',
      id: rootFolderId.value
    });

    if (breadcrumbItems.length > 0) {
      breadcrumbItems[breadcrumbItems.length - 1].disabled = true;
    }

    return breadcrumbItems;
  });

  const getTeamName = (teamId) => {
    return teamsObj.value[teamId] ? teamsObj.value[teamId].name : 'Unknown Team';
  };

  const getUserName = (userId) => {
    return usersObj.value[userId] ? usersObj.value[userId].displayName : 'Unknown User';
  };

  const getUserAvatar = (userId) => {
    return usersObj.value[userId] ? usersObj.value[userId].avatarUrl : null;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRandomColor = (id) => {
    const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
    const index = String(id || '?').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  const formatDate = (dateString, showTime = false) => {
    const date = new Date(dateString);
    return showTime
      ? format(date, 'yyyy-MM-dd HH:mm:ss')
      : format(date, 'yyyy-MM-dd');
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  // buildMode is hardcoded to 'webapp' and $launchUrl is not defined in this
  // repo, so the old 'production' branch could only ever throw (R6)
  const openUrl = (url) => window.open(url, '_blank');

  onMounted(() => {
    if (store.state.settings.projectCode) {
      getData();
    }
  });

  return {
    loading,
    search,
    searchLatest,
    activeTab,
    fileTypeMetric,
    fileTypeRows,
    uploadsMetric,
    uploadsRows,
    teamUserLevel,
    teamUserRows,
    teamUserColorIndex,
    selectTeamUser,
    formatSize,
    formatFileSize,
    teamUserChart,
    fileTypeChart,
    uploadsChart,
    allDocumentsHeaders,
    headers,
    filteredLatestDocuments,
    filteredDocuments,
    recentDocumentsCount,
    totalDocuments,
    totalStorage,
    formatStorage,
    getParentFolderName,
    handleItemClick,
    goToPath,
    breadcrumbs,
    getTeamName,
    getUserName,
    getUserAvatar,
    getInitials,
    getRandomColor,
    formatDate,
    formatTimeAgo
  };
};
